import { useStaggerIn } from "@/components/Reveal";
import shipConsoleLogo from "@/assets/Logos/ShipConsole.webp";
import duckLogo from "@/assets/Logos/duck.webp";
import lovableLogo from "@/assets/Logos/lovable.jpg";
import anthropicLogo from "@/assets/Logos/anthropic.jpg";
import openaiLogo from "@/assets/Logos/openai.jpg";
import outskill from "@/assets/Logos/outskill.jpg";


export const certifications = [
  {
    title: "Vibe Coding",
    issuer: "Lovable",
    date: "2026-02",
    logo: lovableLogo,
    url: "https://www.linkedin.com/in/chiraggadhvi7272/",
  },
  {
    title: "Claude Code in Action",
    issuer: "Anthropic",
    date: "2026-03",
    logo: anthropicLogo,
    url: "https://verify.skilljar.com/c/qrsm6ckuo5w8",
  },
  {
    title: "ChatGPT Foundations for Teachers",
    issuer: "OpenAI",
    date: "2026-01",
    logo: openaiLogo,
    url: "https://www.credly.com/badges/e5d4560a-4abd-43a3-9ec5-3f8ff6f8c913/linked_in_profile",
  },
  {
    title: "Generative AI Mastermind",
    issuer: "Outskill",
    date: "2026-03",
    logo: outskill,
    url: "https://drive.google.com/file/d/1b4ZEerOn_TX8ze6vNMk8kMLT3vWg671N/view?usp=sharing",
  },
];

export const awards = [
  { title: "Best Performer of the Year", meta: "DevOps automation impact at ShipConsole", logo: shipConsoleLogo, url: "https://shipconsole.com/" },
  { title: "Client Performance Bonus", meta: "Fixed a freelance client's urgent production issue", logo: duckLogo },
];

// `page`: the /certifications page (big heading, rows stagger in). Otherwise the home section.
export function Certifications({ page = false }: { page?: boolean }) {
  const scope = useStaggerIn<HTMLDivElement>();
  const stagger = page ? { "data-stagger": "" } : {};
  const subhead = "text-[15px] md:text-base font-semibold tracking-tight text-foreground";
  return (
    <section
      id="certifications"
      className={`relative px-4 sm:px-6 bg-background overflow-hidden flex flex-col items-center ${page ? "pb-20 pt-12 sm:pt-16" : "py-6 md:py-8"}`}
    >
      <div className="site-frame relative z-10" ref={scope}>
        {page && (
          <div className="mb-8 sm:mb-10">
            <h1 {...stagger} className="mb-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Certifications</h1>
            <p {...stagger} className="text-muted-foreground">Courses I've completed and recognition along the way. Tap one to see the credential.</p>
          </div>
        )}

        {/* ── CREDENTIALS / Certifications ─────────────────────── */}
        <div {...stagger} className="mb-4 md:mb-5 text-left">
          {page ? <h2 className={subhead}>Courses</h2> : <h2 className={subhead}>Certifications</h2>}
        </div>

        <RowGrid
          stagger={page}
          items={certifications.map((c) => ({ title: c.title, meta: `(${c.issuer}, ${c.date})`, logo: c.logo, url: c.url }))}
        />

        {/* ── RECOGNITION / Awards ─────────────────────────────── */}
        <div {...stagger} className="mt-12 md:mt-16 mb-4 md:mb-5 text-left">
          <h2 className={subhead}>Awards</h2>
        </div>

        <RowGrid items={awards} stagger={page} />

      </div>
    </section>
  );
}

export type RowItem = { title: string; meta: string; logo: string; url?: string };

/* ── 2-column bordered list, shared with the Apps section ─────────────── */
export function RowGrid({ items, stagger = false }: { items: RowItem[]; stagger?: boolean }) {
  const rows: RowItem[][] = [];
  for (let i = 0; i < items.length; i += 2) rows.push(items.slice(i, i + 2));
  return (
    <div className="border-t border-border/40">
      {rows.map(([left, right]) => (
        <div key={left.title} {...(stagger ? { "data-stagger": "" } : {})} className="grid grid-cols-1 sm:grid-cols-2 border-b border-border/40">
          <Row item={left} hasDivider={!!right} />
          {right ? <Row item={right} hasDivider={false} /> : <div className="sm:border-l sm:border-border/40" />}
        </div>
      ))}
    </div>
  );
}

function Row({ item, hasDivider }: { item: RowItem; hasDivider: boolean }) {
  const className = `flex items-center gap-3 md:gap-4 py-3.5 px-3 md:px-4 transition-colors ${
    item.url ? "group/row" : ""
  } ${hasDivider ? "sm:border-r sm:border-border/40" : ""}`;
  const content = (
    <>
      <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
        <img
          src={item.logo}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain rounded-lg"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-snug truncate">{item.title}</p>
        <p className="text-xs text-foreground/60 mt-0.5">{item.meta}</p>
      </div>
    </>
  );
  return item.url ? (
    <a href={item.url} target="_blank" rel="noopener noreferrer" className={className}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}
